import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';

interface AdminIdea { id: string; ref: string; title: string; body: string; position: number; published: boolean }
interface Lesson { title: string; summary: string; ref: string }
interface Question { id?: string; question: string; answer: string; wrong: string[] }
interface AdminStory { id: string; title: string; ref: string; summary: string; position: number; lessons: Lesson[]; questions: Question[]; published: boolean }

const REVIEW_NOTE = 'Revisé que está escrito con palabras propias y bajo las 16 Verdades Fundamentales de las Asambleas de Dios.';

/**
 * Agrega, edita y publica ideas e historias de «Biblia en minutos».
 * scope «keora»: el Superadmin, para todas las iglesias. scope «church»: quien maneja devocionales, solo para su iglesia.
 */
export function MinutesAdminPage({ scope = 'keora' }: { scope?: 'keora' | 'church' }) {
  const me = useMe();
  const churchId = me.activeChurchId;
  const allowed = scope === 'keora' ? me.isPlatformAdmin : Boolean(churchId && me.can('devotional.manage'));
  const base = scope === 'keora' ? '/bible/admin/minutes' : `/bible/churches/${churchId}/minutes/content`;
  const data = useAsync((signal) => (allowed ? api.get<{ ideas: AdminIdea[]; stories: AdminStory[] }>(base, signal) : Promise.resolve(null)), [allowed, base]);
  const [editing, setEditing] = useState<{ kind: 'idea'; item: AdminIdea | null } | { kind: 'story'; item: AdminStory | null } | null>(null);
  if (!allowed) return <Navigate to="/app/biblia/minutos" replace />;
  if (data.status === 'loading') return <Loading />;
  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  const { ideas, stories } = data.data!;
  const done = () => (setEditing(null), data.reload());
  return (
    <div className="page page--narrow bible-wide">
      <header className="page__header">
        <p className="small">
          <Link to="/app/biblia/minutos">← Biblia en minutos</Link>
        </p>
        <h1>{scope === 'keora' ? 'Contenido de Biblia en minutos' : 'Biblia en minutos de mi iglesia'}</h1>
        <p className="small muted">
          {scope === 'keora' ? 'Es para todas las iglesias. ' : 'Solo lo ven los miembros de tu iglesia, junto con el contenido de KEORA. '}
          Cada idea o historia nueva queda en borrador hasta que la revisas y la publicas. Si cambias el texto de una publicada, vuelve a borrador.
        </p>
      </header>
      {editing?.kind === 'idea' ? <IdeaForm base={base} item={editing.item} onDone={done} onCancel={() => setEditing(null)} /> : null}
      {editing?.kind === 'story' ? <StoryForm base={base} item={editing.item} onDone={done} onCancel={() => setEditing(null)} /> : null}
      {!editing ? (
        <>
          <Card title={`Ideas (${ideas.length})`} action={<Button variant="ghost" onClick={() => setEditing({ kind: 'idea', item: null })}>Agregar idea</Button>}>
            <ul className="list">
              {ideas.map((i) => (
                <ContentRow base={base} key={i.id} kind="idea" id={i.id} title={i.title} detail={`${i.ref} · ${i.body}`} published={i.published} onEdit={() => setEditing({ kind: 'idea', item: i })} onChange={data.reload} />
              ))}
            </ul>
          </Card>
          <Card title={`Historias (${stories.length})`} action={<Button variant="ghost" onClick={() => setEditing({ kind: 'story', item: null })}>Agregar historia</Button>}>
            <ul className="list">
              {stories.map((s) => (
                <ContentRow base={base} key={s.id} kind="story" id={s.id} title={s.title} detail={`${s.ref} · ${s.lessons.length} lecciones · ${s.questions.length} preguntas`} published={s.published} onEdit={() => setEditing({ kind: 'story', item: s })} onChange={data.reload} />
              ))}
            </ul>
          </Card>
        </>
      ) : null}
    </div>
  );
}

function ContentRow({ base, kind, id, title, detail, published, onEdit, onChange }: { base: string; kind: 'idea' | 'story'; id: string; title: string; detail: string; published: boolean; onEdit: () => void; onChange: () => void }) {
  const [reviewing, setReviewing] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = (p: Promise<unknown>) => p.then(() => (setReviewing(false), onChange())).catch((e: unknown) => setError(errorMessage(e)));
  return (
    <li className="list__item">
      <strong>{title}</strong> <span className={`badge${published ? ' badge--accent' : ''}`}>{published ? 'Publicada' : 'Borrador'}</span>
      <span className="block small muted">{detail}</span>
      {error ? <Alert>{error}</Alert> : null}
      {reviewing ? (
        <div className="form">
          <label className="check">
            <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} /> {REVIEW_NOTE}
          </label>
          <div className="actions">
            <Button disabled={!reviewed} onClick={() => void run(api.post(`${base}/${kind}/${id}/publish`, { published: true, reviewed }))}>
              Publicar
            </Button>
            <Button variant="ghost" onClick={() => setReviewing(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <div className="actions actions--wrap">
          <Button variant="ghost" onClick={onEdit}>
            Editar
          </Button>
          {published ? (
            <Button variant="ghost" onClick={() => void run(api.post(`${base}/${kind}/${id}/publish`, { published: false }))}>
              Pasar a borrador
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => (setReviewed(false), setReviewing(true))}>
              Revisar y publicar
            </Button>
          )}
          <Button variant="ghost" onClick={() => void (window.confirm(`¿Quitar «${title}»? Ya no se mostrará a nadie.`) && run(api.delete(`${base}/${kind}/${id}`)))}>
            Quitar
          </Button>
        </div>
      )}
    </li>
  );
}

function TextArea({ label, name, defaultValue, maxLength, rows = 3, required = true }: { label: string; name: string; defaultValue?: string; maxLength: number; rows?: number; required?: boolean }) {
  return (
    <div className="field">
      <label htmlFor={`m-${name}`}>{label}</label>
      <textarea id={`m-${name}`} name={name} defaultValue={defaultValue} maxLength={maxLength} rows={rows} required={required} />
    </div>
  );
}

function IdeaForm({ base, item, onDone, onCancel }: { base: string; item: AdminIdea | null; onDone: () => void; onCancel: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const body = { ref: String(f.get('ref')), title: String(f.get('title')), body: String(f.get('body')), position: f.get('position') ? Number(f.get('position')) : null };
    setBusy(true);
    setError(null);
    try {
      if (item) await api.put(`${base}/ideas/${item.id}`, body);
      else await api.post(`${base}/ideas`, body);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title={item ? 'Editar idea' : 'Nueva idea'}>
      <form className="form" onSubmit={submit}>
        {error ? <Alert>{error}</Alert> : null}
        <Field label="Cita" name="ref" required maxLength={60} defaultValue={item?.ref} placeholder="Mateo 7:12" />
        <Field label="Título corto" name="title" required maxLength={80} defaultValue={item?.title} />
        <TextArea label="Enseñanza (2 o 3 líneas, con palabras propias; no copies el versículo)" name="body" maxLength={420} defaultValue={item?.body} />
        <Field label="Orden (opcional)" name="position" type="number" min={0} defaultValue={item?.position} />
        <div className="actions">
          <Button type="submit" busy={busy}>
            Guardar
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}

const emptyLesson = (): Lesson => ({ title: '', summary: '', ref: '' });
const emptyQuestion = (): Question => ({ question: '', answer: '', wrong: ['', '', ''] });

function StoryForm({ base, item, onDone, onCancel }: { base: string; item: AdminStory | null; onDone: () => void; onCancel: () => void }) {
  const [lessons, setLessons] = useState<Lesson[]>(item?.lessons ?? [emptyLesson(), emptyLesson(), emptyLesson()]);
  const [questions, setQuestions] = useState<Question[]>(item?.questions.map((q) => ({ ...q, wrong: [...q.wrong, '', '', ''].slice(0, 3) })) ?? [emptyQuestion(), emptyQuestion(), emptyQuestion(), emptyQuestion()]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const setLesson = (i: number, k: keyof Lesson, v: string) => setLessons((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const setQ = (i: number, patch: Partial<Question>) => setQuestions((qs) => qs.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const body = {
      title: String(f.get('title')), ref: String(f.get('ref')), summary: String(f.get('summary') ?? ''),
      position: f.get('position') ? Number(f.get('position')) : null,
      lessons,
      questions: questions.map((q) => ({ ...(q.id ? { id: q.id } : {}), question: q.question, answer: q.answer, wrong: q.wrong.map((w) => w.trim()).filter(Boolean) })),
    };
    setBusy(true);
    setError(null);
    try {
      if (item) await api.put(`${base}/stories/${item.id}`, body);
      else await api.post(`${base}/stories`, body);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title={item ? 'Editar historia' : 'Nueva historia'}>
      <form className="form" onSubmit={submit}>
        {error ? <Alert>{error}</Alert> : null}
        <Field label="Título" name="title" required maxLength={80} defaultValue={item?.title} placeholder="Daniel en el foso" />
        <Field label="Cita" name="ref" required maxLength={60} defaultValue={item?.ref} placeholder="Daniel 6" />
        <Field label="Resumen de una línea" name="summary" maxLength={200} defaultValue={item?.summary} />
        <Field label="Orden (opcional)" name="position" type="number" min={0} defaultValue={item?.position} />
        <h3>Lecciones</h3>
        {lessons.map((l, i) => (
          <fieldset key={i} className="minutes__fieldset">
            <legend>Lección {i + 1}</legend>
            <Field label="Título" value={l.title} onChange={(e) => setLesson(i, 'title', e.target.value)} required maxLength={80} />
            <Field label="Cita" value={l.ref} onChange={(e) => setLesson(i, 'ref', e.target.value)} required maxLength={60} />
            <div className="field">
              <label htmlFor={`ls-${i}`}>Resumen (palabras propias)</label>
              <textarea id={`ls-${i}`} value={l.summary} onChange={(e) => setLesson(i, 'summary', e.target.value)} required maxLength={420} rows={3} />
            </div>
            {lessons.length > 1 ? (
              <Button type="button" variant="ghost" onClick={() => setLessons((ls) => ls.filter((_, j) => j !== i))}>
                Quitar lección
              </Button>
            ) : null}
          </fieldset>
        ))}
        {lessons.length < 8 ? (
          <Button type="button" variant="secondary" onClick={() => setLessons((ls) => [...ls, emptyLesson()])}>
            Agregar lección
          </Button>
        ) : null}
        <h3>Preguntas</h3>
        <p className="small muted">De 3 a 8. En cada sesión salen 3 y van rotando; las que alguien falla salen primero en su repaso.</p>
        {questions.map((q, i) => (
          <fieldset key={i} className="minutes__fieldset">
            <legend>Pregunta {i + 1}</legend>
            <Field label="Pregunta" value={q.question} onChange={(e) => setQ(i, { question: e.target.value })} required maxLength={300} />
            <Field label="Respuesta correcta" value={q.answer} onChange={(e) => setQ(i, { answer: e.target.value })} required maxLength={150} />
            {q.wrong.map((w, k) => (
              <Field key={k} label={`Opción incorrecta ${k + 1}${k > 0 ? ' (opcional)' : ''}`} value={w} onChange={(e) => setQ(i, { wrong: q.wrong.map((x, j) => (j === k ? e.target.value : x)) })} required={k === 0} maxLength={150} />
            ))}
            {questions.length > 3 ? (
              <Button type="button" variant="ghost" onClick={() => setQuestions((qs) => qs.filter((_, j) => j !== i))}>
                Quitar pregunta
              </Button>
            ) : null}
          </fieldset>
        ))}
        {questions.length < 8 ? (
          <Button type="button" variant="secondary" onClick={() => setQuestions((qs) => [...qs, emptyQuestion()])}>
            Agregar pregunta
          </Button>
        ) : null}
        <div className="actions">
          <Button type="submit" busy={busy}>
            Guardar
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}
